import { useEffect, useRef, type ReactNode } from "react";
import type { Profile } from "../shared/types";
import { Logo } from "./components";

export type Page =
  | "home"
  | "workout"
  | "history"
  | "stats"
  | "routine"
  | "settings"
  | "profiles";
const nav: Array<{ page: Page; label: string; icon: string }> = [
  { page: "home", label: "Home", icon: "⌂" },
  { page: "history", label: "History", icon: "◷" },
  { page: "stats", label: "Stats", icon: "↗" },
  { page: "routine", label: "Routine", icon: "≡" },
  { page: "settings", label: "Settings", icon: "⚙" },
];

export function Shell({
  profile,
  page,
  focusKey,
  mainLabel,
  onNavigate,
  onSwitchProfile,
  children,
  offlineCount = 0,
  syncProblem = false,
}: {
  profile: Profile;
  page: Page;
  focusKey: string;
  mainLabel: string;
  onNavigate: (page: Page) => void;
  onSwitchProfile: () => void;
  children: ReactNode;
  offlineCount?: number;
  syncProblem?: boolean;
}) {
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    mainRef.current?.focus();
  }, [focusKey]);
  return (
    <div className="app-shell">
      <header className="topbar">
        <button
          className="logo-button"
          onClick={() => onNavigate("home")}
          aria-label="Go to Home"
        >
          <Logo compact />
        </button>
        <div className="topbar__actions">
          {offlineCount > 0 && (
            <span
              className={`sync-pill${syncProblem ? " sync-pill--error" : ""}`}
              role="status"
            >
              {syncProblem ? "Sync needs attention" : "Saved on phone"} ·{" "}
              {offlineCount}
            </span>
          )}
          <button
            className="profile-button"
            onClick={() =>
              onNavigate(profile.isAdmin ? "profiles" : "settings")
            }
            aria-label="Open profile settings"
          >
            <span aria-hidden="true">
              {profile.displayName[0]?.toUpperCase()}
            </span>
            <small>{profile.displayName}</small>
          </button>
          <button
            className="text-button profile-switch"
            onClick={onSwitchProfile}
          >
            Switch profile
          </button>
        </div>
      </header>
      <main
        id="main"
        className="page"
        ref={mainRef}
        tabIndex={-1}
        aria-label={mainLabel}
      >
        {children}
      </main>
      {page !== "workout" && (
        <nav className="bottom-nav" aria-label="Primary">
          {nav.map((item) => (
            <button
              key={item.page}
              aria-current={page === item.page ? "page" : undefined}
              onClick={() => onNavigate(item.page)}
            >
              <span aria-hidden="true">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}
